import { FC } from 'react';
import { createPortal } from 'react-dom';
import Popup from '../../../../../../Popup/Popup';
import { ProductAttributeInterface } from '../../../../../../../../Interface/ProductAttribute';
import { OnlineshopProductImages } from '../../../../../../../../Interface/OnlineshopProductImages';
import useViewports from '../../../../../../../Hooks/useViewports';
import useValidImages from '../../../../../../../Hooks/useValidImages';
import QuickViewDesktopGallery from './QuickViewDesktopGallery';
import QuickViewMobileGallery from './QuickViewMobileGallery';
import QuickViewProductInfo from './QuickViewProductInfo';
import styles from './ProductQuickViewModal.module.scss?module';
import { getFlavourDiscount } from '../../../../../../../Helper/flavourDiscount';

interface ProductQuickViewModalProps {
  show: boolean;
  onHide: () => void;
  attribute: ProductAttributeInterface;
  images: OnlineshopProductImages;
}

const ProductQuickViewModal: FC<ProductQuickViewModalProps> = ({
  show,
  onHide,
  attribute,
  images,
}) => {
  const { xs, sm } = useViewports();
  const isMobile = xs || sm;
  const validImages = useValidImages(images);
  const productSize = attribute.product_size;

  const discount = getFlavourDiscount(attribute);
  const isClearanceFlavour = discount.isClearance;
  const clearanceLabel = discount.tooltip;
  const showRegularPrice =
    productSize?.price_show_regular || isClearanceFlavour;
  const isPriceRed = productSize?.price_is_red || isClearanceFlavour;
  const displayPrice =
    isClearanceFlavour && discount.combinedPrice != null
      ? discount.combinedPrice
      : productSize?.current_price;
  const isDepositProduct =
    attribute.product_size?.deposit_units_count > 0 &&
    attribute.product_size?.single_deposit_price > 0;

  const badgesProps = {
    isDepositProduct,
    isClearanceFlavour,
    clearanceLabel,
    isNew: !!productSize?.is_new,
    hasPromo: !!(
      productSize?.promo_text &&
      ((productSize?.promo_percent ?? 0) > 0 ||
        (!isClearanceFlavour &&
          ((productSize?.promo_plus_condition_count ?? 0) > 0 ||
            (productSize?.promo_free_product_count ?? 0) > 0)))
    ),
    promoText: productSize?.promo_text,
    imageOverlayUri: productSize?.image_overlay_uri,
  };

  return createPortal(
    <Popup
      size="xl"
      dialogClassName={styles.QuickViewDialog}
      contentClassName={styles.QuickViewContent}
      centered
      show={show}
      onHide={onHide}
    >
      {show && (
        <>
          <Popup.Header closeButton className={styles.Header}>
            <span className={styles.HeaderTitle}>Product Details</span>
          </Popup.Header>
          <Popup.Body
            className={isMobile ? styles.BodyMobile : styles.BodyDesk}
          >
            {isMobile ? (
              <QuickViewMobileGallery
                images={validImages}
                badgesProps={badgesProps}
              />
            ) : (
              <QuickViewDesktopGallery
                images={validImages}
                badgesProps={badgesProps}
              />
            )}

            <QuickViewProductInfo
              attribute={attribute}
              productSize={productSize}
              showRegularPrice={showRegularPrice}
              isPriceRed={isPriceRed}
              displayPrice={displayPrice}
            />
          </Popup.Body>
        </>
      )}
    </Popup>,
    document.body
  );
};

export default ProductQuickViewModal;
